import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';
import { Theme, useTheme } from '@mui/material/styles';
import { Icon, Tooltip, IconButton, Box, Typography, CircularProgress } from '@mui/material';
import styles from 'assets/jss/material-kit-react/views/profilePage.js';
import BubbleContainer from 'components/BubbleContainer/BubbleContainer';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Button from 'components/CustomButtons/Button.js';
import Card from 'components/Card/Card.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardBody from 'components/Card/CardBody.js';
import NavPills from 'components/NavPills/NavPills.js';
import { Link } from 'react-router-dom';
import {
  FestivalOutlined,
  MenuBook,
  YoutubeSearchedForOutlined,
  BookmarkBorderOutlined,
  ShoppingCartCheckoutOutlined,
  ConstructionOutlined,
  HandshakeOutlined,
  LoginOutlined,
  PersonAddOutlined,
  PersonOutline,
  WarningAmberOutlined,
  AddShoppingCartOutlined,
  ArrowRightOutlined,
  LockClockOutlined,
  WallpaperOutlined,
  QueryStatsOutlined,
  GavelOutlined,
  Share as ShareIcon,
  Bookmark as BookmarkIcon,
  ShoppingCart as ShoppingCartIcon,
  ContentCopy as ContentCopyIcon,
  // LocalOffer as LocalOfferIcon,
  // SupportAgent as SupportAgentIcon,
} from '@mui/icons-material';
import { secondaryColor } from '../../assets/jss/material-kit-react';
import { useHeaderLinks } from 'components/Header/HeaderLinksContext';
import d2rsIconBlock from 'assets/img/d2rsiconblock.png';
// import d2rshopLogo from 'assets/img/d2rshopicon.png';
import d2ricon from 'assets/img/d2r-logo.png';
import axios from 'axios';
import config from '../../config/config.json';
import { getColorFromRarity } from '../../utils/rarityUtils';
import blueIcon from '../../assets/img/blue_icon.webp';
import yellowIcon from '../../assets/img/yellow_icon.png';
import hardcoreOff from '../../assets/img/hardcore_off.png';
import hardcoreOn from '../../assets/img/hardcore_on.png';
import ladderOn from '../../assets/img/ladder_on.png';
import ladderOff from '../../assets/img/ladder_off.png';
import anyClass from '../../assets/img/pvp.png';
import barbarianClass from '../../assets/img/barb.png';
import sorceressClass from '../../assets/img/soso.png';
import amazonClass from '../../assets/img/ama.png';
import assassinClass from '../../assets/img/assa.png';
import necromancerClass from '../../assets/img/nec.png';
import paladinClass from '../../assets/img/pala.png';
import druidClass from '../../assets/img/dudu.png';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCreateTracker,
  useDeleteTracker,
  useTrackedItems,
  isItemTracked,
} from '../../stores/trackedItems';
import {
  useCreateProductTracker,
  useDeleteProductTrackerByProduct,
  useTrackedProducts,
  isProductTracked,
} from '../../stores/trackedProducts';
import toast from 'react-hot-toast';
import PaginationControls from '../../components/PaginationControls/PaginationControls';
import { notifyCartItemAdded } from '../../utils/cartUtils';

interface ItemData {
  id: number;
  name: string;
  title: string;
  context: string;
  d2rshopproductlink: string;
  fiat_price: string;
  product_image_path: string;
  skin_image_path: string;
  original_rarity: string;
  stats_description: string;
  status?: 'in_cart' | 'not_in_cart';
  is_product: boolean;
}

const useStyles = makeStyles<Theme>((theme) => ({
  mainContainer: {
    position: 'relative',
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    borderRadius: '6px',
    color: theme.palette.text.primary,
  },
  discordButton: {
    color: '#7289da !important',
    borderColor: '#7289da !important',
    '&:hover': {
      borderColor: '#FFD700 !important',
    },
  },
  container: {
    ...styles.container,
    position: 'relative',
    zIndex: 3,
  },
  contentContainer: {
    marginLeft: 'auto',
    marginRight: 'auto',
    textAlign: 'center',
    zIndex: 1100,
    '& .MuiTabs-root': {
      zIndex: 1200,
    },
    '& .MuiTab-root': {
      zIndex: 1200,
    },
    '& .MuiTabs-root + div': {
      marginTop: 0,
    },
  },
  cardHeader: {
    textAlign: 'center',
    '& h4': {
      marginTop: '0',
      marginBottom: '0',
      fontSize: '2rem',
    },
  },
  featureCard: {
    padding: '20px',
    borderRadius: '6px',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    border: '1px solid rgba(241, 198, 43, 0.3)',
  },
  mainCard: {
    padding: '20px',
    borderRadius: '6px',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    marginBottom: '2rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    border: '1px solid rgba(241, 198, 43, 0.3)',
  },
  featureIcon: {
    fontSize: '48px',
    marginBottom: '20px',
    color: theme.palette.primary.main,
  },
  section: {
    marginBottom: '2rem',
  },
  warningContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '10px',
    marginBottom: '20px',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '4px',
    color: theme.palette.primary.contrastText,
    '& .MuiSvgIcon-root': {
      color: theme.palette.warning.main,
    },
  },
  navPillsContainer: {
    marginBottom: '2rem',
  },
  card: {
    marginTop: '30px',
  },
}));

export default function LandingPage() {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const { isLoggedIn, userData } = useHeaderLinks();
  useDocumentTitle('D2RS');
  const queryClient = useQueryClient();
  const { data: trackedItems = [] } = useTrackedItems();
  const { data: trackedProducts = [] } = useTrackedProducts();
  const createItemTracker = useCreateTracker();
  const deleteItemTracker = useDeleteTracker();
  const createProductTracker = useCreateProductTracker();
  const deleteProductTracker = useDeleteProductTrackerByProduct();
  const sessionToken = localStorage.getItem('session_token');

  const handleTabChange = () => {};

  const mainContent = (
    <div className={classes.section}>
      <div className={classes.mainCard}>
        {isLoggedIn ? (
          <div style={{ marginBottom: '20px' }}>
            <h4>{t('landing.welcomeUser')}</h4>
            <h4>
              {userData?.profile_image ? (
                <img
                  src={userData.profile_image}
                  alt="profile"
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    verticalAlign: 'middle',
                    marginRight: '8px',
                  }}
                />
              ) : (
                <PersonOutline sx={{ fontSize: 24, verticalAlign: 'middle', marginRight: '8px' }} />
              )}{' '}
              {userData?.user?.name || ''}
            </h4>
          </div>
        ) : (
          <div style={{ marginBottom: '20px' }}>
            <h4>
              <PersonOutline sx={{ fontSize: 24, verticalAlign: 'middle', marginRight: '8px' }} />
              {t('landing.welcomeUser')} {t('landing.visitor')}
            </h4>
            <p style={{ marginTop: '10px', fontSize: '0.9rem', color: 'inherit' }}>
              {t('landing.developmentNotice')}
            </p>
            <Link to="/login">
              <Button color="outlined" size="sm">
                <LoginOutlined style={{ marginRight: '5px', color: 'inherit' }} />
                {t('navigation.login')}
                <ArrowRightOutlined style={{ marginLeft: '5px', color: 'inherit' }} />
              </Button>
            </Link>
            <Link to="/register">
              <Button color="outlined" size="sm" style={{ marginLeft: '5px' }}>
                <PersonAddOutlined style={{ marginRight: '5px', color: 'inherit' }} />
                {t('navigation.register')}
                <ArrowRightOutlined style={{ marginLeft: '5px', color: 'inherit' }} />
              </Button>
            </Link>
            <div style={{ marginTop: '10px' }}>
              <a
                href={`https://discord.com/api/oauth2/authorize?client_id=${config.discord_client_id}&redirect_uri=${encodeURIComponent(
                  `${window.location.origin}/auth/discord/callback`
                )}&response_type=code&scope=identify%20email`}
                style={{ textDecoration: 'none' }}
              >
                <Button color="outlined" size="sm" className={classes.discordButton}>
                  <Icon
                    className="fab fa-discord"
                    style={{ marginRight: '5px', fontSize: '16px', color: '#7289da' }}
                  />
                  {t('navigation.signInWithDiscord')}
                  <ArrowRightOutlined style={{ marginLeft: '5px', color: '#7289da' }} />
                </Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const featuresContent = (
    <div className={classes.section}>
      <GridContainer>
        <GridItem xs={12} sm={12} md={6}>
          <div className={classes.featureCard}>
            <ShoppingCartCheckoutOutlined
              className={classes.featureIcon}
              style={{ color: secondaryColor }}
            />
            <h4>{t('landing.features.instantBuy.title')}</h4>
            <p>{t('landing.features.instantBuy.description')}</p>
            <div style={{ marginTop: '10px' }}>
              <Link to="/shop">
                <Button color="outlined" size="sm">
                  <ShoppingCartCheckoutOutlined style={{ marginRight: '5px', color: 'inherit' }} />
                  {t('navigation.instantShop')}
                  <ArrowRightOutlined style={{ marginLeft: '5px', color: 'inherit' }} />
                </Button>
              </Link>
            </div>
          </div>
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <div className={classes.featureCard}>
            <FestivalOutlined className={classes.featureIcon} style={{ color: secondaryColor }} />
            <h4>{t('landing.features.search.title')}</h4>
            <p>{t('landing.features.search.description')}</p>
            <div style={{ marginTop: '10px' }}>
              <Link to="/tour-pages?t=tours">
                <Button color="outlined" size="sm">
                  <FestivalOutlined style={{ marginRight: '5px', color: 'inherit' }} />
                  {t('landing.tour')}
                  <ArrowRightOutlined style={{ marginLeft: '5px', color: 'inherit' }} />
                </Button>
              </Link>
              <Link to="/guides">
                <Button color="outlined" size="sm" style={{ marginLeft: '5px' }}>
                  <MenuBook style={{ marginRight: '5px', color: 'inherit' }} />
                  {t('landing.build')}
                  <ArrowRightOutlined style={{ marginLeft: '5px', color: 'inherit' }} />
                </Button>
              </Link>
            </div>
            <div style={{ marginTop: '10px' }}>
              <Link to="/get-involved">
                <Button color="outlined" size="sm" style={{ marginLeft: '5px' }}>
                  <HandshakeOutlined style={{ marginRight: '5px', color: 'inherit' }} />
                  {t('landing.work')}
                  <ArrowRightOutlined style={{ marginLeft: '5px', color: 'inherit' }} />
                </Button>
              </Link>
            </div>
          </div>
        </GridItem>
        <GridItem xs={12} sm={12} md={12}>
          <div className={classes.featureCard}>
            <ConstructionOutlined
              className={classes.featureIcon}
              style={{ color: secondaryColor }}
            />
            <h4>{t('landing.features.uniqueTools.title')}</h4>
            <p>{t('landing.features.uniqueTools.description')}</p>
            <div style={{ marginTop: '10px' }}>
              <Link to="/offers-page?t=howtouse">
                <Button color="outlined" size="sm">
                  <GavelOutlined style={{ marginRight: '5px', color: 'inherit' }} />
                  {t('navigation.offers')}
                  <ArrowRightOutlined style={{ marginLeft: '5px', color: 'inherit' }} />
                </Button>
              </Link>
              <Link to="/saved-searches?t=howToUse">
                <Button color="outlined" size="sm" style={{ marginLeft: '5px' }}>
                  <YoutubeSearchedForOutlined style={{ marginRight: '5px', color: 'inherit' }} />
                  {t('navigation.savedSearches')}
                  <ArrowRightOutlined style={{ marginLeft: '5px', color: 'inherit' }} />
                </Button>
              </Link>
              <Link to="/tracker?t=howToUse">
                <Button color="outlined" size="sm" style={{ marginLeft: '5px' }}>
                  <BookmarkBorderOutlined style={{ marginRight: '5px', color: 'inherit' }} />
                  {t('navigation.itemTracker')}
                  <ArrowRightOutlined style={{ marginLeft: '5px', color: 'inherit' }} />
                </Button>
              </Link>
              <Link to="/event-mod">
                <Button color="outlined" size="sm" style={{ marginLeft: '5px' }}>
                  <FestivalOutlined style={{ marginRight: '5px', color: 'inherit' }} />
                  {t('navigation.eventMod')}
                  <ArrowRightOutlined style={{ marginLeft: '5px', color: 'inherit' }} />
                </Button>
              </Link>
            </div>
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                <LockClockOutlined
                  style={{ fontSize: '24px', marginBottom: '10px', opacity: 0.7 }}
                />
              </Tooltip>
              <div style={{ marginTop: '10px' }}>
                <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                  <span>
                    <Button color="outlined" size="sm" disabled>
                      <WallpaperOutlined style={{ marginRight: '5px', color: 'inherit' }} />
                      {t('navigation.imageGenerator')}
                      <LockClockOutlined style={{ marginLeft: '5px', fontSize: '16px' }} />
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                  <span>
                    <Button color="outlined" size="sm" style={{ marginLeft: '5px' }} disabled>
                      <QueryStatsOutlined style={{ marginRight: '5px', color: 'inherit' }} />
                      {t('navigation.priceIndex')}
                      <LockClockOutlined style={{ marginLeft: '5px', fontSize: '16px' }} />
                    </Button>
                  </span>
                </Tooltip>
                <br />
                <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                  <span>
                    <Button color="outlined" size="sm" style={{ marginLeft: '5px' }} disabled>
                      <Icon
                        className="fab fa-discord"
                        style={{ marginRight: '5px', fontSize: '16px' }}
                      />
                      {t('navigation.discordCompanion')}
                      <LockClockOutlined style={{ marginLeft: '5px', fontSize: '16px' }} />
                    </Button>
                  </span>
                </Tooltip>
              </div>
            </div>
          </div>
        </GridItem>
      </GridContainer>
    </div>
  );

  const [topRareItems, setTopRareItems] = useState<ItemData[]>([]);
  const [loadingRareItems, setLoadingRareItems] = useState(true);
  const [topRunes, setTopRunes] = useState<ItemData[]>([]);
  const [loadingRunes, setLoadingRunes] = useState(true);
  const [sharedLink, setSharedLink] = useState('');
  const [sharedItemId, setSharedItemId] = useState<number | null>(null);
  const [currentRareItemPage, setCurrentRareItemPage] = useState(1);
  const rareItemsPerPage = 4;

  useEffect(() => {
    const fetchTopRareItems = async () => {
      try {
        const response = await axios.post(
          config.backend_base_url + 'public/pss/search',
          {
            search_term: ',rare',
          },
          { headers: { 'session-token': sessionToken } }
        );
        if (response.data && response.data.length > 0) {
          setTopRareItems(response.data);
        }
      } catch (error) {
        toast.error(t('search.errors.fetchFailed', 'Failed to fetch rare items'));
      } finally {
        setLoadingRareItems(false);
      }
    };

    const fetchTopRunes = async () => {
      try {
        // Fetch Jah runes
        const jahResponse = await axios.post(
          config.backend_base_url + 'ml/pickit/dynamic/psearch',
          {
            search_term: '.jah rune',
          },
          { headers: { 'session-token': sessionToken } }
        );

        // Fetch Ber runes
        const berResponse = await axios.post(
          config.backend_base_url + 'ml/pickit/dynamic/psearch',
          {
            search_term: '.ber rune',
          },
          { headers: { 'session-token': sessionToken } }
        );

        // Fetch Lo runes
        const loResponse = await axios.post(
          config.backend_base_url + 'ml/pickit/dynamic/psearch',
          {
            search_term: '.lo rune',
          },
          { headers: { 'session-token': sessionToken } }
        );

        // Fetch Sur runes
        const surResponse = await axios.post(
          config.backend_base_url + 'ml/pickit/dynamic/psearch',
          {
            search_term: '.sur rune',
          },
          { headers: { 'session-token': sessionToken } }
        );

        // Map the data for each rune type, ensuring we get up to 4 of each
        const mappedJahRunes = Array.isArray(jahResponse.data)
          ? jahResponse.data.slice(0, 4).map((rune) => ({
              id: rune.id,
              name: rune.title || rune.name,
              title: rune.title || rune.name,
              context: rune.ladder
                ? rune.hardcore
                  ? 'hcl'
                  : 'scl'
                : rune.hardcore
                  ? 'hcnl'
                  : 'scnl',
              product_image_path: rune.product_image_path || rune.image_path,
              skin_image_path: rune.skin_image_path,
              d2rshopproductlink: rune.shop_product_link || '',
              fiat_price:
                rune.fg_price !== '???'
                  ? ((parseFloat(rune.fg_price) / 166) * (rune.shop_price_factor || 1)).toFixed(2)
                  : '???',
              ladder: rune.ladder,
              hardcore: rune.hardcore,
              original_rarity: 'RUNE',
              stats_description: rune.stats || '',
              is_product: true,
            }))
          : [];

        const mappedBerRunes = Array.isArray(berResponse.data)
          ? berResponse.data.slice(0, 4).map((rune) => ({
              id: rune.id,
              name: rune.title || rune.name,
              title: rune.title || rune.name,
              context: rune.ladder
                ? rune.hardcore
                  ? 'hcl'
                  : 'scl'
                : rune.hardcore
                  ? 'hcnl'
                  : 'scnl',
              product_image_path: rune.product_image_path || rune.image_path,
              skin_image_path: rune.skin_image_path,
              d2rshopproductlink: rune.shop_product_link || '',
              fiat_price:
                rune.fg_price !== '???'
                  ? ((parseFloat(rune.fg_price) / 166) * (rune.shop_price_factor || 1)).toFixed(2)
                  : '???',
              ladder: rune.ladder,
              hardcore: rune.hardcore,
              original_rarity: 'RUNE',
              stats_description: rune.stats || '',
              is_product: true,
            }))
          : [];

        const mappedSurRunes = Array.isArray(surResponse.data)
          ? surResponse.data.slice(0, 4).map((rune) => ({
              id: rune.id,
              name: rune.title || rune.name,
              title: rune.title || rune.name,
              context: rune.ladder
                ? rune.hardcore
                  ? 'hcl'
                  : 'scl'
                : rune.hardcore
                  ? 'hcnl'
                  : 'scnl',
              product_image_path: rune.product_image_path || rune.image_path,
              skin_image_path: rune.skin_image_path,
              d2rshopproductlink: rune.shop_product_link || '',
              fiat_price:
                rune.fg_price !== '???'
                  ? ((parseFloat(rune.fg_price) / 166) * (rune.shop_price_factor || 1)).toFixed(2)
                  : '???',
              ladder: rune.ladder,
              hardcore: rune.hardcore,
              original_rarity: 'RUNE',
              stats_description: rune.stats || '',
              is_product: true,
            }))
          : [];

        const mappedLoRunes = Array.isArray(loResponse.data)
          ? loResponse.data.slice(0, 4).map((rune) => ({
              id: rune.id,
              name: rune.title || rune.name,
              title: rune.title || rune.name,
              context: rune.ladder
                ? rune.hardcore
                  ? 'hcl'
                  : 'scl'
                : rune.hardcore
                  ? 'hcnl'
                  : 'scnl',
              product_image_path: rune.product_image_path || rune.image_path,
              skin_image_path: rune.skin_image_path,
              d2rshopproductlink: rune.shop_product_link || '',
              fiat_price:
                rune.fg_price !== '???'
                  ? ((parseFloat(rune.fg_price) / 166) * (rune.shop_price_factor || 1)).toFixed(2)
                  : '???',
              ladder: rune.ladder,
              hardcore: rune.hardcore,
              original_rarity: 'RUNE',
              stats_description: rune.stats || '',
              is_product: true,
            }))
          : [];

        // Combine all runes in the specified order: Jah, Ber, Sur, Lo
        const combinedRunes = [
          ...mappedJahRunes,
          ...mappedBerRunes,
          ...mappedSurRunes,
          ...mappedLoRunes,
        ];

        // Sort runes by context: SCL, SCNL, HCL, HCNL
        const contextOrder = {
          scl: 0,
          scnl: 1,
          hcl: 2,
          hcnl: 3,
        };

        const sortedRunes = combinedRunes.sort((a, b) => {
          const contextA = a.context.toLowerCase();
          const contextB = b.context.toLowerCase();
          return contextOrder[contextA] - contextOrder[contextB];
        });

        if (sortedRunes.length > 0) {
          setTopRunes(sortedRunes);
        }
      } catch (error) {
        toast.error(t('search.errors.fetchFailed', 'Failed to fetch runes'));
      } finally {
        setLoadingRunes(false);
      }
    };

    fetchTopRareItems();
    fetchTopRunes();
  }, [sessionToken, t]);

  const topRareItemsContent = (
    <Card className={classes.card}>
      <CardHeader color="primary" className={classes.cardHeader}>
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}
        >
          <h4>{t('landing.topRareItems')}</h4>
        </div>
      </CardHeader>
      <CardBody>
        <Link to="/shop?term=,rare">
          <Button color="outlined" size="sm">
            <ShoppingCartCheckoutOutlined style={{ marginRight: '5px', color: 'inherit' }} />
            {t('common.viewAll')}
            <ArrowRightOutlined style={{ marginLeft: '5px', color: 'inherit' }} />
          </Button>
        </Link>
        <PaginationControls
          data={topRareItems}
          currentPage={currentRareItemPage}
          setCurrentPage={setCurrentRareItemPage}
          itemsPerPage={4}
        />
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '24px',
            justifyContent: 'center',
            alignItems: 'flex-start',
            padding: '24px',
            width: '100%',
          }}
        >
          {loadingRareItems ? (
            <div style={{ textAlign: 'center', width: '100%' }}>
              <CircularProgress />
            </div>
          ) : (
            <GridContainer spacing={3}>
              {topRareItems
                .slice(
                  (currentRareItemPage - 1) * rareItemsPerPage,
                  currentRareItemPage * rareItemsPerPage
                )
                .map((item, key) => (
                  <GridItem xs={12} sm={6} md={3} key={key}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: '8px',
                        backgroundColor: 'rgba(0,0,0,0.05)',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        height: '100%',
                      }}
                    >
                      <div
                        style={{
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '8px',
                          padding: '4px',
                          borderRadius: '4px',
                          border: '1px solid rgba(0,0,0,0.1)',
                          backgroundColor: 'rgba(0,0,0,0.02)',
                          marginBottom: '8px',
                        }}
                      >
                        <Tooltip title="These tools can be used on this item">
                          <IconButton
                            size="small"
                            sx={{
                              color: '#FFD700',
                              '&:hover': {
                                color: '#FFD700',
                              },
                            }}
                          >
                            <ConstructionOutlined fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                        <div
                          style={{
                            display: 'flex',
                            gap: '4px',
                            flex: 1,
                            justifyContent: 'center',
                          }}
                        >
                          <Tooltip title={t('search.item.viewDetails', 'Details')}>
                            <IconButton
                              onClick={() =>
                                window.open(
                                  item.original_rarity === 'RUNE'
                                    ? `/p/${item.id}`
                                    : `/i/${item.id}`,
                                  '_blank'
                                )
                              }
                              size="small"
                              sx={{
                                color: (theme) => theme.palette.text.secondary,
                                '&:hover': {
                                  color: (theme) => theme.palette.text.primary,
                                },
                              }}
                            >
                              <img
                                src={blueIcon}
                                alt="details"
                                style={{ width: '20px', height: '20px' }}
                              />
                            </IconButton>
                          </Tooltip>
                          {item.d2rshopproductlink && (
                            <Tooltip title={t('search.vendor.viewInShop', 'View in Shop')}>
                              <IconButton
                                onClick={() => window.open(item.d2rshopproductlink, '_blank')}
                                size="small"
                                sx={{
                                  color: (theme) => theme.palette.text.secondary,
                                  '&:hover': {
                                    color: (theme) => theme.palette.text.primary,
                                  },
                                }}
                              >
                                <img
                                  src={yellowIcon}
                                  alt="shop"
                                  style={{ width: '20px', height: '20px' }}
                                />
                              </IconButton>
                            </Tooltip>
                          )}
                          {sessionToken && item.context !== 'all' && (
                            <Tooltip
                              title={
                                item.status === 'in_cart'
                                  ? t('search.cart.takeFromCart')
                                  : t('search.cart.addToCart')
                              }
                            >
                              <IconButton
                                onClick={() => cartItem(event, item.id)}
                                size="small"
                                sx={{
                                  color: (theme) => theme.palette.text.secondary,
                                  '&:hover': {
                                    color: (theme) => theme.palette.text.primary,
                                  },
                                }}
                              >
                                <ShoppingCartIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip
                            title={
                              item.is_product
                                ? isProductTracked(trackedProducts, Number(item.id))
                                  ? t('common.untrack')
                                  : t('common.track')
                                : isItemTracked(trackedItems, Number(item.id))
                                  ? t('common.untrack')
                                  : t('common.track')
                            }
                          >
                            <IconButton
                              onClick={() => handleTrackToggle(item)}
                              size="small"
                              sx={{
                                width: '32px',
                                height: '32px',
                                backgroundColor: item.is_product
                                  ? isProductTracked(trackedProducts, Number(item.id))
                                    ? '#FFD700'
                                    : 'transparent'
                                  : isItemTracked(trackedItems, Number(item.id))
                                    ? '#FFD700'
                                    : 'transparent',
                                '&:hover': {
                                  backgroundColor: item.is_product
                                    ? isProductTracked(trackedProducts, Number(item.id))
                                      ? '#FFD700'
                                      : 'rgba(0, 0, 0, 0.04)'
                                    : isItemTracked(trackedItems, Number(item.id))
                                      ? '#FFD700'
                                      : 'rgba(0, 0, 0, 0.04)',
                                },
                              }}
                            >
                              {item.is_product ? (
                                isProductTracked(trackedProducts, Number(item.id)) ? (
                                  <BookmarkIcon sx={{ color: '#000000' }} />
                                ) : (
                                  <BookmarkBorderOutlined
                                    sx={{ color: (theme) => theme.palette.text.secondary }}
                                  />
                                )
                              ) : isItemTracked(trackedItems, Number(item.id)) ? (
                                <BookmarkIcon sx={{ color: '#000000' }} />
                              ) : (
                                <BookmarkBorderOutlined
                                  sx={{ color: (theme) => theme.palette.text.secondary }}
                                />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('search.offers.makeOfferAmount')}>
                            <IconButton
                              onClick={() => priceOffer(event, item.id)}
                              size="small"
                              sx={{
                                color: (theme) => theme.palette.text.secondary,
                                '&:hover': {
                                  color: (theme) => theme.palette.text.primary,
                                },
                              }}
                            >
                              <GavelOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('common.share')}>
                            <IconButton
                              onClick={() => handleShare(item)}
                              size="small"
                              sx={{
                                color: (theme) => theme.palette.text.secondary,
                                '&:hover': {
                                  color: (theme) => theme.palette.text.primary,
                                },
                              }}
                            >
                              <ShareIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </div>
                        <div style={{ width: '32px' }} /> {/* Spacer */}
                      </div>
                      <div style={{ width: '100%', textAlign: 'center', marginBottom: '8px' }}>
                        {sharedLink && sharedItemId === item.id && (
                          <Box
                            sx={{
                              p: 1,
                              mb: 1,
                              backgroundColor: (theme) =>
                                theme.palette.mode === 'dark'
                                  ? theme.palette.grey[900]
                                  : theme.palette.grey[100],
                              borderRadius: 1,
                              whiteSpace: 'pre-wrap',
                              fontFamily: 'monospace',
                              color: (theme) => theme.palette.text.primary,
                              position: 'relative',
                              fontSize: '0.8rem',
                            }}
                          >
                            {sharedLink}
                            <Tooltip title={t('common.copyToClipboard')} placement="top">
                              <IconButton
                                onClick={() => {
                                  navigator.clipboard.writeText(sharedLink);
                                  toast.success(t('common.copiedToClipboard'));
                                }}
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  top: -24,
                                  right: -8,
                                  color: (theme) => theme.palette.text.secondary,
                                  '&:hover': {
                                    color: (theme) => theme.palette.text.primary,
                                  },
                                }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </div>
                      <Box
                        sx={{
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'dark'
                              ? theme.palette.grey[900]
                              : theme.palette.grey[100],
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: (theme) => `1px solid ${theme.palette.secondary.main}`,
                          display: 'inline-flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          position: 'relative',
                          width: '100%',
                        }}
                      >
                        {item?.context && item.context !== '' && (
                          <Tooltip
                            title={
                              <div>
                                {item.context.toLowerCase() === 'scl' && 'Softcore Ladder'}
                                {item.context.toLowerCase() === 'scnl' && 'Softcore Non-Ladder'}
                                {item.context.toLowerCase() === 'hcl' && 'Hardcore Ladder'}
                                {item.context.toLowerCase() === 'hcnl' && 'Hardcore Non-Ladder'}
                              </div>
                            }
                            arrow
                          >
                            <span
                              style={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '2px',
                                zIndex: 1,
                              }}
                            >
                              {(item.context.toLowerCase() === 'scl' ||
                                item.context.toLowerCase() === 'scnl') && (
                                <img
                                  style={{ width: '24px' }}
                                  src={hardcoreOff}
                                  alt="hardcoreOff"
                                />
                              )}
                              {(item.context.toLowerCase() === 'hcl' ||
                                item.context.toLowerCase() === 'hcnl') && (
                                <img style={{ width: '24px' }} src={hardcoreOn} alt="hardcoreOn" />
                              )}
                              {(item.context.toLowerCase() === 'scl' ||
                                item.context.toLowerCase() === 'hcl') && (
                                <img style={{ width: '24px' }} src={ladderOn} alt="ladderOn" />
                              )}
                              {(item.context.toLowerCase() === 'scnl' ||
                                item.context.toLowerCase() === 'hcnl') && (
                                <img style={{ width: '24px' }} src={ladderOff} alt="ladderOff" />
                              )}
                            </span>
                          </Tooltip>
                        )}
                        {item.d2rshopproductlink !== '' &&
                          item.fiat_price !== '???' &&
                          !isNaN(parseFloat(item.fiat_price)) && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                backgroundColor: (theme) => theme.palette.primary.main,
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '0.875rem',
                                fontWeight: 'bold',
                                zIndex: 1,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                              }}
                            >
                              {parseFloat(item.fiat_price).toFixed(2).replaceAll('.00', '')}$
                            </Box>
                          )}
                        <Tooltip
                          title={
                            <img
                              src={'https://d2r-member.com/image/' + item.product_image_path}
                              alt={item.title || item.name}
                              style={{
                                objectFit: 'contain',
                                maxWidth: '100%',
                                maxHeight: '300px',
                              }}
                            />
                          }
                          arrow
                        >
                          <button
                            onClick={() => handleImageClick(item)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                handleImageClick(item);
                              }
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              cursor: 'pointer',
                              width: '100%',
                            }}
                          >
                            <img
                              src={'https://d2r-member.com/skin/' + item.skin_image_path}
                              alt={item.name}
                              style={{
                                width: 'auto',
                                height: 'auto',
                                maxWidth: '100%',
                                objectFit: 'scale-down',
                                maxHeight: '200px',
                                minHeight: '200px',
                              }}
                            />
                          </button>
                        </Tooltip>
                        <Box
                          sx={{
                            p: 2,
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'monospace',
                            color: '#000',
                            position: 'relative',
                            borderTop: (theme) => `1px solid ${theme.palette.secondary.main}`,
                            backgroundColor: (theme) =>
                              theme.palette.mode === 'dark'
                                ? theme.palette.grey[900]
                                : theme.palette.grey[100],
                            width: 'fit-content',
                            maxWidth: '200%',
                            margin: '0 auto',
                            minHeight: '300px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            component="pre"
                            sx={{
                              margin: 0,
                              fontFamily: 'monospace',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              color: '#000',
                              textAlign: 'center',
                              width: '100%',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                mb: 1,
                              }}
                            >
                              <Tooltip title={item.original_rarity || 'Normal'} placement="top">
                                <Box
                                  sx={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: getColorFromRarity(item.original_rarity),
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                  }}
                                />
                              </Tooltip>
                              {item.name}
                            </Box>
                            <Box sx={{ position: 'relative' }}>
                              {item.stats_description}
                              {rareItemsPerPage === 4 && item.original_rarity !== 'RUNE' && (
                                <Tooltip title={t('common.copyToClipboard')}>
                                  <IconButton
                                    onClick={() => {
                                      navigator.clipboard.writeText(item.stats_description);
                                      toast.success(t('common.copiedToClipboard'));
                                    }}
                                    size="small"
                                    sx={{
                                      position: 'absolute',
                                      top: -24,
                                      right: -8,
                                      color: (theme) => theme.palette.text.secondary,
                                      '&:hover': {
                                        color: (theme) => theme.palette.text.primary,
                                      },
                                    }}
                                  >
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </Typography>
                        </Box>
                      </Box>
                    </div>
                  </GridItem>
                ))}
            </GridContainer>
          )}
        </div>
        <PaginationControls
          data={topRareItems}
          currentPage={currentRareItemPage}
          setCurrentPage={setCurrentRareItemPage}
          itemsPerPage={4}
        />
      </CardBody>
    </Card>
  );

  const topRunesContent = (
    <Card className={classes.card}>
      <CardHeader color="primary" className={classes.cardHeader}>
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}
        >
          <h4>{t('landing.topRunes')}</h4>
        </div>
      </CardHeader>
      <CardBody>
        <GridContainer spacing={3}>
          <GridItem xs={12}>
            <Link to="/shop?term=,rune">
              <Button color="outlined" size="sm">
                <AddShoppingCartOutlined style={{ marginRight: '5px', color: 'inherit' }} />
                {t('common.viewAll')}
                <ArrowRightOutlined style={{ marginLeft: '5px', color: 'inherit' }} />
              </Button>
            </Link>
          </GridItem>
          {loadingRunes ? (
            <GridItem xs={12}>
              <div style={{ textAlign: 'center', width: '100%' }}>
                <CircularProgress />
              </div>
            </GridItem>
          ) : (
            topRunes.map((item, key) => (
              <GridItem xs={12} sm={6} md={4} xl={3} key={key}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '8px',
                    backgroundColor: 'rgba(0,0,0,0.05)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    height: '100%',
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px',
                      padding: '4px',
                      borderRadius: '4px',
                      border: '1px solid rgba(0,0,0,0.1)',
                      backgroundColor: 'rgba(0,0,0,0.02)',
                      marginBottom: '8px',
                    }}
                  >
                    <Tooltip title="These tools can be used on this item">
                      <IconButton
                        size="small"
                        sx={{
                          color: '#FFD700',
                          '&:hover': {
                            color: '#FFD700',
                          },
                        }}
                      >
                        <ConstructionOutlined fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                    <div
                      style={{
                        display: 'flex',
                        gap: '4px',
                        flex: 1,
                        justifyContent: 'center',
                      }}
                    >
                      <Tooltip title={t('search.item.viewDetails', 'Details')}>
                        <IconButton
                          onClick={() =>
                            window.open(
                              item.original_rarity === 'RUNE' ? `/p/${item.id}` : `/i/${item.id}`,
                              '_blank'
                            )
                          }
                          size="small"
                          sx={{
                            color: (theme) => theme.palette.text.secondary,
                            '&:hover': {
                              color: (theme) => theme.palette.text.primary,
                            },
                          }}
                        >
                          <img
                            src={blueIcon}
                            alt="details"
                            style={{ width: '20px', height: '20px' }}
                          />
                        </IconButton>
                      </Tooltip>
                      {item.d2rshopproductlink && (
                        <Tooltip title={t('search.vendor.viewInShop', 'View in Shop')}>
                          <IconButton
                            onClick={() => window.open(item.d2rshopproductlink, '_blank')}
                            size="small"
                            sx={{
                              color: (theme) => theme.palette.text.secondary,
                              '&:hover': {
                                color: (theme) => theme.palette.text.primary,
                              },
                            }}
                          >
                            <img
                              src={yellowIcon}
                              alt="shop"
                              style={{ width: '20px', height: '20px' }}
                            />
                          </IconButton>
                        </Tooltip>
                      )}
                      {sessionToken && item.context !== 'all' && (
                        <Tooltip
                          title={
                            item.status === 'in_cart'
                              ? t('search.cart.takeFromCart')
                              : t('search.cart.addToCart')
                          }
                        >
                          <IconButton
                            onClick={() => cartItem(event, item.id)}
                            size="small"
                            sx={{
                              color: (theme) => theme.palette.text.secondary,
                              '&:hover': {
                                color: (theme) => theme.palette.text.primary,
                              },
                            }}
                          >
                            <ShoppingCartIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip
                        title={
                          item.is_product
                            ? isProductTracked(trackedProducts, Number(item.id))
                              ? t('common.untrack')
                              : t('common.track')
                            : isItemTracked(trackedItems, Number(item.id))
                              ? t('common.untrack')
                              : t('common.track')
                        }
                      >
                        <IconButton
                          onClick={() => handleTrackToggle(item)}
                          size="small"
                          sx={{
                            width: '32px',
                            height: '32px',
                            backgroundColor: item.is_product
                              ? isProductTracked(trackedProducts, Number(item.id))
                                ? '#FFD700'
                                : 'transparent'
                              : isItemTracked(trackedItems, Number(item.id))
                                ? '#FFD700'
                                : 'transparent',
                            '&:hover': {
                              backgroundColor: item.is_product
                                ? isProductTracked(trackedProducts, Number(item.id))
                                  ? '#FFD700'
                                  : 'rgba(0, 0, 0, 0.04)'
                                : isItemTracked(trackedItems, Number(item.id))
                                  ? '#FFD700'
                                  : 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          {item.is_product ? (
                            isProductTracked(trackedProducts, Number(item.id)) ? (
                              <BookmarkIcon sx={{ color: '#000000' }} />
                            ) : (
                              <BookmarkBorderOutlined
                                sx={{ color: (theme) => theme.palette.text.secondary }}
                              />
                            )
                          ) : isItemTracked(trackedItems, Number(item.id)) ? (
                            <BookmarkIcon sx={{ color: '#000000' }} />
                          ) : (
                            <BookmarkBorderOutlined
                              sx={{ color: (theme) => theme.palette.text.secondary }}
                            />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('search.offers.makeOfferAmount')}>
                        <IconButton
                          onClick={() => priceOffer(event, item.id)}
                          size="small"
                          sx={{
                            color: (theme) => theme.palette.text.secondary,
                            '&:hover': {
                              color: (theme) => theme.palette.text.primary,
                            },
                          }}
                        >
                          <GavelOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.share')}>
                        <IconButton
                          onClick={() => handleShare(item)}
                          size="small"
                          sx={{
                            color: (theme) => theme.palette.text.secondary,
                            '&:hover': {
                              color: (theme) => theme.palette.text.primary,
                            },
                          }}
                        >
                          <ShareIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                    <div style={{ width: '32px' }} /> {/* Spacer */}
                  </div>
                  <div style={{ width: '100%', textAlign: 'center', marginBottom: '8px' }}>
                    {sharedLink && sharedItemId === item.id && (
                      <Box
                        sx={{
                          p: 1,
                          mb: 1,
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'dark'
                              ? theme.palette.grey[900]
                              : theme.palette.grey[100],
                          borderRadius: 1,
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'monospace',
                          color: (theme) => theme.palette.text.primary,
                          position: 'relative',
                          fontSize: '0.8rem',
                        }}
                      >
                        {sharedLink}
                        <Tooltip title={t('common.copyToClipboard')} placement="top">
                          <IconButton
                            onClick={() => {
                              navigator.clipboard.writeText(sharedLink);
                              toast.success(t('common.copiedToClipboard'));
                            }}
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: -24,
                              right: -8,
                              color: (theme) => theme.palette.text.secondary,
                              '&:hover': {
                                color: (theme) => theme.palette.text.primary,
                              },
                            }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </div>
                  <Box
                    sx={{
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? theme.palette.grey[900]
                          : theme.palette.grey[100],
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: (theme) => `1px solid ${theme.palette.secondary.main}`,
                      display: 'inline-flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      position: 'relative',
                      width: '100%',
                    }}
                  >
                    {item?.context && item.context !== '' && (
                      <Tooltip
                        title={
                          <div>
                            {item.context.toLowerCase() === 'scl' && 'Softcore Ladder'}
                            {item.context.toLowerCase() === 'scnl' && 'Softcore Non-Ladder'}
                            {item.context.toLowerCase() === 'hcl' && 'Hardcore Ladder'}
                            {item.context.toLowerCase() === 'hcnl' && 'Hardcore Non-Ladder'}
                          </div>
                        }
                        arrow
                      >
                        <span
                          style={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '2px',
                            zIndex: 1,
                          }}
                        >
                          {(item.context.toLowerCase() === 'scl' ||
                            item.context.toLowerCase() === 'scnl') && (
                            <img style={{ width: '24px' }} src={hardcoreOff} alt="hardcoreOff" />
                          )}
                          {(item.context.toLowerCase() === 'hcl' ||
                            item.context.toLowerCase() === 'hcnl') && (
                            <img style={{ width: '24px' }} src={hardcoreOn} alt="hardcoreOn" />
                          )}
                          {(item.context.toLowerCase() === 'scl' ||
                            item.context.toLowerCase() === 'hcl') && (
                            <img style={{ width: '24px' }} src={ladderOn} alt="ladderOn" />
                          )}
                          {(item.context.toLowerCase() === 'scnl' ||
                            item.context.toLowerCase() === 'hcnl') && (
                            <img style={{ width: '24px' }} src={ladderOff} alt="ladderOff" />
                          )}
                        </span>
                      </Tooltip>
                    )}
                    {item.d2rshopproductlink !== '' &&
                      item.fiat_price !== '???' &&
                      !isNaN(parseFloat(item.fiat_price)) && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: (theme) => theme.palette.primary.main,
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '0.875rem',
                            fontWeight: 'bold',
                            zIndex: 1,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          }}
                        >
                          {parseFloat(item.fiat_price).toFixed(2).replaceAll('.00', '')}$
                        </Box>
                      )}
                    <Tooltip
                      title={
                        <img
                          src={'https://d2r-member.com/image/' + item.product_image_path}
                          alt={item.title || item.name}
                          style={{
                            objectFit: 'contain',
                            maxWidth: '100%',
                            maxHeight: '300px',
                          }}
                        />
                      }
                      arrow
                    >
                      <button
                        onClick={() => handleImageClick(item)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleImageClick(item);
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          width: '100%',
                        }}
                      >
                        <img
                          src={'https://d2r-member.com/skin/' + item.skin_image_path}
                          alt={item.name}
                          style={{
                            width: 'auto',
                            height: 'auto',
                            maxWidth: '100%',
                            maxHeight: '200px',
                            minHeight: '200px',
                            objectFit: 'scale-down',
                          }}
                        />
                      </button>
                    </Tooltip>
                    <Box
                      sx={{
                        p: 2,
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                        color: '#000',
                        position: 'relative',
                        borderTop: (theme) => `1px solid ${theme.palette.secondary.main}`,
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'dark'
                            ? theme.palette.grey[900]
                            : theme.palette.grey[100],
                        width: 'fit-content',
                        maxWidth: '200%',
                        margin: '0 auto',
                      }}
                    >
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{
                          margin: 0,
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          color: '#000',
                          textAlign: 'center',
                          width: '100%',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Tooltip title={item.original_rarity || 'Normal'} placement="top">
                            <Box
                              sx={{
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                backgroundColor: getColorFromRarity(item.original_rarity),
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                              }}
                            />
                          </Tooltip>
                          {item.name}
                        </Box>
                        <Box sx={{ position: 'relative' }}>
                          {item.stats_description}
                          {rareItemsPerPage === 4 && item.original_rarity !== 'RUNE' && (
                            <Tooltip title={t('common.copyToClipboard')}>
                              <IconButton
                                onClick={() => {
                                  navigator.clipboard.writeText(item.stats_description);
                                  toast.success(t('common.copiedToClipboard'));
                                }}
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  top: -24,
                                  right: -8,
                                  color: (theme) => theme.palette.text.secondary,
                                  '&:hover': {
                                    color: (theme) => theme.palette.text.primary,
                                  },
                                }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Typography>
                    </Box>
                  </Box>
                </div>
              </GridItem>
            ))
          )}
        </GridContainer>
      </CardBody>
    </Card>
  );

  function priceOffer(event, itemID) {
    event.preventDefault();

    if (!sessionToken) {
      toast.error(t('auth.mustLoginToOffer'));
      return;
    }

    const amount = window.prompt(t('search.offers.enterAmount'));
    if (!amount) {
      return;
    }

    const numericAmount = parseInt(amount, 10);
    if (isNaN(numericAmount) || numericAmount <= 0) {
      toast.error(t('search.errors.invalidOffer'));
      return;
    }

    axios
      .post(
        config.backend_base_url + 'pss/offer',
        {
          item_id: parseInt(itemID, 10),
          offer: numericAmount.toString(),
        },
        { headers: { 'session-token': sessionToken } }
      )
      .then((_) => {
        toast.success(t('search.item.offerCreated'));
        queryClient.invalidateQueries({ queryKey: ['trackedItems'] });
        queryClient.invalidateQueries({ queryKey: ['trackedProducts'] });
        queryClient.refetchQueries({ queryKey: ['trackedItems'] });
        queryClient.refetchQueries({ queryKey: ['trackedProducts'] });
      })
      .catch((_error) => {
        toast.error(
          t('search.errors.createOfferFailed', 'Failed to create offer. Please try again')
        );
      });
  }

  function cartItem(event, itemID) {
    event.preventDefault();
    if (isNaN(itemID)) {
      toast.error(t('search.errors.invalidId', 'Invalid item ID'));
      return;
    }
    const url = config.backend_base_url + 'd2rs/cart/add';
    const loading = toast.loading(t('search.cart.adding', 'Adding item to cart'));

    axios
      .post(
        url,
        {
          item_ids: [itemID],
        },
        { headers: { 'session-token': sessionToken } }
      )
      .then((response) => {
        if (response.status) {
          toast.dismiss(loading);
          toast.success(t('search.cart.success', 'Successfully added item to cart'));

          // Notify components about cart update
          queryClient.invalidateQueries({ queryKey: ['cartItems'] });
          notifyCartItemAdded({ id: itemID });
        }
      })
      .catch((_error) => {
        toast.dismiss(loading);
        toast.error(t('search.errors.cartAddFailed', 'Failed to add item to cart'));
      });
  }

  const handleShare = (item) => {
    const baseUrl = window.location.origin;
    const url = item.is_product ? `${baseUrl}/p/${item.id}` : `${baseUrl}/i/${item.id}`;
    setSharedLink(url);
    setSharedItemId(item.id);
    navigator.clipboard
      .writeText(url)
      .then(() => {
        toast.success(t('common.copiedToClipboard'));
      })
      .catch(() => {
        toast.error(t('common.clipboardError'));
      });
  };

  const handleTrackToggle = (item) => {
    if (!sessionToken) {
      toast.error(t('auth.mustLoginToTrack'));
      return;
    }

    if (item.is_product) {
      // Handle product tracking
      const isTracked = isProductTracked(trackedProducts, Number(item.id));
      if (isTracked) {
        deleteProductTracker.mutate(
          { product_id: Number(item.id) },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: ['trackedProducts'] });
              queryClient.refetchQueries({ queryKey: ['trackedProducts'] });
            },
          }
        );
      } else {
        createProductTracker.mutate(
          { product_id: Number(item.id) },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: ['trackedProducts'] });
              queryClient.refetchQueries({ queryKey: ['trackedProducts'] });
            },
          }
        );
      }
    } else {
      // Handle item tracking
      const isTracked = isItemTracked(trackedItems, Number(item.id));
      if (isTracked) {
        const tracker = trackedItems.find(
          (trackedItem) => Number(trackedItem.item_id) === Number(item.id)
        );
        if (tracker) {
          deleteItemTracker.mutate(
            { id: tracker.id },
            {
              onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['trackedItems'] });
                queryClient.refetchQueries({ queryKey: ['trackedItems'] });
              },
            }
          );
        } else {
          toast.error(t('common.errors.trackerNotFound'));
        }
      } else {
        createItemTracker.mutate(
          { item_id: Number(item.id) },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: ['trackedItems'] });
              queryClient.refetchQueries({ queryKey: ['trackedItems'] });
            },
          }
        );
      }
    }
  };

  const handleImageClick = (item) => {
    if (item.is_product || item.original_rarity === 'RUNE') {
      window.open(`/p/${item.id}`, '_blank');
    } else {
      window.open(`/i/${item.id}`, '_blank');
    }
  };

  return (
    <div>
      <div className={classes.mainContainer}>
        <div className={classes.container}>
          <div className={classes.contentContainer}>
            <Card>
              <CardHeader color="primary" className={classes.cardHeader}>
                <h4>{t('landing.title')}</h4>
              </CardHeader>
              <div className={classes.warningContainer}>
                <WarningAmberOutlined />
                <span>{t('landing.disclaimer')}</span>
              </div>
              <CardBody>
                <div className={classes.navPillsContainer}>
                  <NavPills
                    color="primary"
                    queryKey="t"
                    onChange={handleTabChange}
                    alignCenter
                    tabs={[
                      {
                        tabButton: (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <img src={d2rsIconBlock} alt="D2RS" style={{ height: '48px' }} />
                            <span
                              style={{ fontWeight: 700, fontSize: '24px', color: secondaryColor }}
                            >
                              ×
                            </span>
                            <img
                              src={d2ricon}
                              alt="Diablo II: Resurrected"
                              style={{ height: '48px' }}
                            />
                          </div>
                        ),
                        tabContent: (
                          <span>
                            {mainContent}
                            {featuresContent}
                          </span>
                        ),
                        tabName: 'shop',
                      },
                      // {
                      //   tabButton: (
                      //     <img src={d2rshopLogo} alt="D2RShop" style={{ height: '48px' }} />
                      //   ),
                      //   tabContent: (
                      //     <span>
                      //       <div className={classes.section}>
                      //         <div className={classes.mainCard}>
                      //           <h4>Welcome to D2RShop</h4>
                      //           <p>
                      //             Your one-stop shop for Diablo 2: Resurrected items and services.
                      //             Browse our extensive collection of items, services, and more.
                      //           </p>
                      //           <div style={{ marginTop: '20px' }}>
                      //             <a
                      //               href="https://d2rshop.com"
                      //               target="_blank"
                      //               rel="noopener noreferrer"
                      //             >
                      //               <Button color="outlined" size="sm">
                      //                 <img
                      //                   src={d2rshopLogo}
                      //                   alt="D2RShop"
                      //                   style={{
                      //                     height: '20px',
                      //                     marginRight: '5px',
                      //                     verticalAlign: 'middle',
                      //                   }}
                      //                 />
                      //                 Visit D2RShop.com
                      //                 <ArrowRightOutlined
                      //                   style={{ marginLeft: '5px', color: 'inherit' }}
                      //                 />
                      //               </Button>
                      //             </a>
                      //           </div>
                      //         </div>
                      //       </div>
                      //       <div className={classes.section}>
                      //         <GridContainer>
                      //           <GridItem xs={12} sm={12} md={6}>
                      //             <div className={classes.featureCard}>
                      //               <LocalOfferIcon
                      //                 className={classes.featureIcon}
                      //                 style={{ color: secondaryColor }}
                      //               />
                      //               <h4>Best Prices</h4>
                      //               <p>
                      //                 We combine automation with manual expertise to offer the best
                      //                 prices in the market. Our rare items are carefully curated and
                      //                 listed by knowledgeable D2R players who understand true item
                      //                 value.
                      //               </p>
                      //             </div>
                      //           </GridItem>
                      //           <GridItem xs={12} sm={12} md={6}>
                      //             <div className={classes.featureCard}>
                      //               <SupportAgentIcon
                      //                 className={classes.featureIcon}
                      //                 style={{ color: secondaryColor }}
                      //               />
                      //               <h4>Hardworking Team</h4>
                      //               <p>
                      //                 Our team is available 24/7 to assist you. We're committed to
                      //                 providing exceptional service and efficient order fulfillment.
                      //               </p>
                      //             </div>
                      //           </GridItem>
                      //         </GridContainer>
                      //       </div>
                      //     </span>
                      //   ),
                      //   tabName: 'product-shop',
                      // },
                    ]}
                  />
                </div>
              </CardBody>
            </Card>
            {topRareItemsContent}
            {topRunesContent}
            <Card className={classes.card}>
              <CardHeader color="primary" className={classes.cardHeader}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '16px',
                  }}
                >
                  <h4>{t('landing.fullChars')}</h4>
                </div>
              </CardHeader>
              <CardBody>
                <Link to="/tour-pages?t=tours">
                  <Button color="outlined" size="sm">
                    <FestivalOutlined style={{ marginRight: '5px', color: 'inherit' }} />
                    {t('landing.tour')}
                    <ArrowRightOutlined style={{ marginLeft: '5px', color: 'inherit' }} />
                  </Button>
                </Link>
                <Link to="/guides">
                  <Button color="outlined" size="sm">
                    <MenuBook style={{ marginRight: '5px', color: 'inherit' }} />
                    {t('landing.build')}
                    <ArrowRightOutlined style={{ marginLeft: '5px', color: 'inherit' }} />
                  </Button>
                </Link>
                <NavPills
                  color="primary"
                  queryKey="class"
                  alignCenter
                  tabs={[
                    {
                      tabButton: (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <img
                            src={anyClass}
                            alt="General"
                            style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                          />
                          {t('guides.class.general')}
                        </div>
                      ),
                      tabContent: (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <NavPills
                            color="primary"
                            queryKey="category"
                            alignCenter
                            tabs={[
                              {
                                tabButton: t('landing.fullChars.dedicatedItems'),
                                tabContent: (
                                  <div
                                    style={{
                                      textAlign: 'center',
                                      padding: '20px',
                                      border: '1px solid rgba(241, 198, 43, 0.3)',
                                      borderRadius: '6px',
                                    }}
                                  >
                                    <PaginationControls
                                      data={topRareItems}
                                      currentPage={currentRareItemPage}
                                      setCurrentPage={setCurrentRareItemPage}
                                      itemsPerPage={8}
                                    />
                                    <GridContainer spacing={3}>
                                      {loadingRareItems ? (
                                        <GridItem xs={12}>
                                          <div style={{ textAlign: 'center', width: '100%' }}>
                                            <CircularProgress />
                                          </div>
                                        </GridItem>
                                      ) : (
                                        topRareItems
                                          .slice(
                                            (currentRareItemPage - 1) * 8,
                                            currentRareItemPage * 8
                                          )
                                          .map((item, key) => (
                                            <GridItem xs={12} sm={6} md={3} key={key}>
                                              <div
                                                style={{
                                                  display: 'flex',
                                                  flexDirection: 'column',
                                                  alignItems: 'center',
                                                  padding: '8px',
                                                  backgroundColor: 'rgba(0,0,0,0.05)',
                                                  borderRadius: '8px',
                                                  overflow: 'hidden',
                                                  height: '100%',
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    width: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: '8px',
                                                    padding: '4px',
                                                    borderRadius: '4px',
                                                    border: '1px solid rgba(0,0,0,0.1)',
                                                    backgroundColor: 'rgba(0,0,0,0.02)',
                                                    marginBottom: '8px',
                                                  }}
                                                >
                                                  <Tooltip title="These tools can be used on this item">
                                                    <IconButton
                                                      size="small"
                                                      sx={{
                                                        color: '#FFD700',
                                                        '&:hover': {
                                                          color: '#FFD700',
                                                        },
                                                      }}
                                                    >
                                                      <ConstructionOutlined fontSize="inherit" />
                                                    </IconButton>
                                                  </Tooltip>
                                                  <div
                                                    style={{
                                                      display: 'flex',
                                                      gap: '4px',
                                                      flex: 1,
                                                      justifyContent: 'center',
                                                    }}
                                                  >
                                                    <Tooltip
                                                      title={t(
                                                        'search.item.viewDetails',
                                                        'Details'
                                                      )}
                                                    >
                                                      <IconButton
                                                        onClick={() =>
                                                          window.open(
                                                            item.original_rarity === 'RUNE'
                                                              ? `/p/${item.id}`
                                                              : `/i/${item.id}`,
                                                            '_blank'
                                                          )
                                                        }
                                                        size="small"
                                                        sx={{
                                                          color: (theme) =>
                                                            theme.palette.text.secondary,
                                                          '&:hover': {
                                                            color: (theme) =>
                                                              theme.palette.text.primary,
                                                          },
                                                        }}
                                                      >
                                                        <img
                                                          src={blueIcon}
                                                          alt="details"
                                                          style={{ width: '20px', height: '20px' }}
                                                        />
                                                      </IconButton>
                                                    </Tooltip>
                                                    {item.d2rshopproductlink && (
                                                      <Tooltip
                                                        title={t(
                                                          'search.vendor.viewInShop',
                                                          'View in Shop'
                                                        )}
                                                      >
                                                        <IconButton
                                                          onClick={() =>
                                                            window.open(
                                                              item.d2rshopproductlink,
                                                              '_blank'
                                                            )
                                                          }
                                                          size="small"
                                                          sx={{
                                                            color: (theme) =>
                                                              theme.palette.text.secondary,
                                                            '&:hover': {
                                                              color: (theme) =>
                                                                theme.palette.text.primary,
                                                            },
                                                          }}
                                                        >
                                                          <img
                                                            src={yellowIcon}
                                                            alt="shop"
                                                            style={{
                                                              width: '20px',
                                                              height: '20px',
                                                            }}
                                                          />
                                                        </IconButton>
                                                      </Tooltip>
                                                    )}
                                                    {sessionToken && item.context !== 'all' && (
                                                      <Tooltip
                                                        title={
                                                          item.status === 'in_cart'
                                                            ? t('search.cart.takeFromCart')
                                                            : t('search.cart.addToCart')
                                                        }
                                                      >
                                                        <IconButton
                                                          onClick={() => cartItem(event, item.id)}
                                                          size="small"
                                                          sx={{
                                                            color: (theme) =>
                                                              theme.palette.text.secondary,
                                                            '&:hover': {
                                                              color: (theme) =>
                                                                theme.palette.text.primary,
                                                            },
                                                          }}
                                                        >
                                                          <ShoppingCartIcon fontSize="small" />
                                                        </IconButton>
                                                      </Tooltip>
                                                    )}
                                                    <Tooltip
                                                      title={
                                                        item.is_product
                                                          ? isProductTracked(
                                                              trackedProducts,
                                                              Number(item.id)
                                                            )
                                                            ? t('common.untrack')
                                                            : t('common.track')
                                                          : isItemTracked(
                                                                trackedItems,
                                                                Number(item.id)
                                                              )
                                                            ? t('common.untrack')
                                                            : t('common.track')
                                                      }
                                                    >
                                                      <IconButton
                                                        onClick={() => handleTrackToggle(item)}
                                                        size="small"
                                                        sx={{
                                                          width: '32px',
                                                          height: '32px',
                                                          backgroundColor: item.is_product
                                                            ? isProductTracked(
                                                                trackedProducts,
                                                                Number(item.id)
                                                              )
                                                              ? '#FFD700'
                                                              : 'transparent'
                                                            : isItemTracked(
                                                                  trackedItems,
                                                                  Number(item.id)
                                                                )
                                                              ? '#FFD700'
                                                              : 'transparent',
                                                          '&:hover': {
                                                            backgroundColor: item.is_product
                                                              ? isProductTracked(
                                                                  trackedProducts,
                                                                  Number(item.id)
                                                                )
                                                                ? '#FFD700'
                                                                : 'rgba(0, 0, 0, 0.04)'
                                                              : isItemTracked(
                                                                    trackedItems,
                                                                    Number(item.id)
                                                                  )
                                                                ? '#FFD700'
                                                                : 'rgba(0, 0, 0, 0.04)',
                                                          },
                                                        }}
                                                      >
                                                        {item.is_product ? (
                                                          isProductTracked(
                                                            trackedProducts,
                                                            Number(item.id)
                                                          ) ? (
                                                            <BookmarkIcon
                                                              sx={{ color: '#000000' }}
                                                            />
                                                          ) : (
                                                            <BookmarkBorderOutlined
                                                              sx={{
                                                                color: (theme) =>
                                                                  theme.palette.text.secondary,
                                                              }}
                                                            />
                                                          )
                                                        ) : isItemTracked(
                                                            trackedItems,
                                                            Number(item.id)
                                                          ) ? (
                                                          <BookmarkIcon sx={{ color: '#000000' }} />
                                                        ) : (
                                                          <BookmarkBorderOutlined
                                                            sx={{
                                                              color: (theme) =>
                                                                theme.palette.text.secondary,
                                                            }}
                                                          />
                                                        )}
                                                      </IconButton>
                                                    </Tooltip>
                                                    <Tooltip
                                                      title={t('search.offers.makeOfferAmount')}
                                                    >
                                                      <IconButton
                                                        onClick={() => priceOffer(event, item.id)}
                                                        size="small"
                                                        sx={{
                                                          color: (theme) =>
                                                            theme.palette.text.secondary,
                                                          '&:hover': {
                                                            color: (theme) =>
                                                              theme.palette.text.primary,
                                                          },
                                                        }}
                                                      >
                                                        <GavelOutlined fontSize="small" />
                                                      </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={t('common.share')}>
                                                      <IconButton
                                                        onClick={() => handleShare(item)}
                                                        size="small"
                                                        sx={{
                                                          color: (theme) =>
                                                            theme.palette.text.secondary,
                                                          '&:hover': {
                                                            color: (theme) =>
                                                              theme.palette.text.primary,
                                                          },
                                                        }}
                                                      >
                                                        <ShareIcon fontSize="small" />
                                                      </IconButton>
                                                    </Tooltip>
                                                  </div>
                                                  <div style={{ width: '32px' }} /> {/* Spacer */}
                                                </div>
                                                <div
                                                  style={{
                                                    width: '100%',
                                                    textAlign: 'center',
                                                    marginBottom: '8px',
                                                  }}
                                                >
                                                  {sharedLink && sharedItemId === item.id && (
                                                    <Box
                                                      sx={{
                                                        p: 1,
                                                        mb: 1,
                                                        backgroundColor: (theme) =>
                                                          theme.palette.mode === 'dark'
                                                            ? theme.palette.grey[900]
                                                            : theme.palette.grey[100],
                                                        borderRadius: 1,
                                                        whiteSpace: 'pre-wrap',
                                                        fontFamily: 'monospace',
                                                        color: (theme) =>
                                                          theme.palette.text.primary,
                                                        position: 'relative',
                                                        fontSize: '0.8rem',
                                                      }}
                                                    >
                                                      {sharedLink}
                                                      <Tooltip
                                                        title={t('common.copyToClipboard')}
                                                        placement="top"
                                                      >
                                                        <IconButton
                                                          onClick={() => {
                                                            navigator.clipboard.writeText(
                                                              sharedLink
                                                            );
                                                            toast.success(
                                                              t('common.copiedToClipboard')
                                                            );
                                                          }}
                                                          size="small"
                                                          sx={{
                                                            position: 'absolute',
                                                            top: -24,
                                                            right: -8,
                                                            color: (theme) =>
                                                              theme.palette.text.secondary,
                                                            '&:hover': {
                                                              color: (theme) =>
                                                                theme.palette.text.primary,
                                                            },
                                                          }}
                                                        >
                                                          <ContentCopyIcon fontSize="small" />
                                                        </IconButton>
                                                      </Tooltip>
                                                    </Box>
                                                  )}
                                                </div>
                                                <Box
                                                  sx={{
                                                    backgroundColor: (theme) =>
                                                      theme.palette.mode === 'dark'
                                                        ? theme.palette.grey[900]
                                                        : theme.palette.grey[100],
                                                    borderRadius: 1,
                                                    overflow: 'hidden',
                                                    border: (theme) =>
                                                      `1px solid ${theme.palette.secondary.main}`,
                                                    display: 'inline-flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    position: 'relative',
                                                    width: '100%',
                                                  }}
                                                >
                                                  {item?.context && item.context !== '' && (
                                                    <Tooltip
                                                      title={
                                                        <div>
                                                          {item.context.toLowerCase() === 'scl' &&
                                                            'Softcore Ladder'}
                                                          {item.context.toLowerCase() === 'scnl' &&
                                                            'Softcore Non-Ladder'}
                                                          {item.context.toLowerCase() === 'hcl' &&
                                                            'Hardcore Ladder'}
                                                          {item.context.toLowerCase() === 'hcnl' &&
                                                            'Hardcore Non-Ladder'}
                                                        </div>
                                                      }
                                                      arrow
                                                    >
                                                      <span
                                                        style={{
                                                          position: 'absolute',
                                                          top: 8,
                                                          left: 8,
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          gap: '2px',
                                                          zIndex: 1,
                                                        }}
                                                      >
                                                        {(item.context.toLowerCase() === 'scl' ||
                                                          item.context.toLowerCase() ===
                                                            'scnl') && (
                                                          <img
                                                            style={{ width: '24px' }}
                                                            src={hardcoreOff}
                                                            alt="hardcoreOff"
                                                          />
                                                        )}
                                                        {(item.context.toLowerCase() === 'hcl' ||
                                                          item.context.toLowerCase() ===
                                                            'hcnl') && (
                                                          <img
                                                            style={{ width: '24px' }}
                                                            src={hardcoreOn}
                                                            alt="hardcoreOn"
                                                          />
                                                        )}
                                                        {(item.context.toLowerCase() === 'scl' ||
                                                          item.context.toLowerCase() === 'hcl') && (
                                                          <img
                                                            style={{ width: '24px' }}
                                                            src={ladderOn}
                                                            alt="ladderOn"
                                                          />
                                                        )}
                                                        {(item.context.toLowerCase() === 'scnl' ||
                                                          item.context.toLowerCase() ===
                                                            'hcnl') && (
                                                          <img
                                                            style={{ width: '24px' }}
                                                            src={ladderOff}
                                                            alt="ladderOff"
                                                          />
                                                        )}
                                                      </span>
                                                    </Tooltip>
                                                  )}
                                                  {item.d2rshopproductlink !== '' &&
                                                    item.fiat_price !== '???' &&
                                                    !isNaN(parseFloat(item.fiat_price)) && (
                                                      <Box
                                                        sx={{
                                                          position: 'absolute',
                                                          top: 8,
                                                          right: 8,
                                                          backgroundColor: (theme) =>
                                                            theme.palette.primary.main,
                                                          color: 'white',
                                                          padding: '4px 8px',
                                                          borderRadius: '4px',
                                                          fontSize: '0.875rem',
                                                          fontWeight: 'bold',
                                                          zIndex: 1,
                                                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                        }}
                                                      >
                                                        {parseFloat(item.fiat_price)
                                                          .toFixed(2)
                                                          .replaceAll('.00', '')}
                                                        $
                                                      </Box>
                                                    )}
                                                  <Tooltip
                                                    title={
                                                      <img
                                                        src={
                                                          'https://d2r-member.com/image/' +
                                                          item.product_image_path
                                                        }
                                                        alt={item.title || item.name}
                                                        style={{
                                                          objectFit: 'contain',
                                                          maxWidth: '100%',
                                                          maxHeight: '300px',
                                                        }}
                                                      />
                                                    }
                                                    arrow
                                                  >
                                                    <button
                                                      onClick={() => handleImageClick(item)}
                                                      onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                          handleImageClick(item);
                                                        }
                                                      }}
                                                      style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        padding: 0,
                                                        cursor: 'pointer',
                                                        width: '100%',
                                                      }}
                                                    >
                                                      <img
                                                        src={
                                                          'https://d2r-member.com/skin/' +
                                                          item.skin_image_path
                                                        }
                                                        alt={item.name}
                                                        style={{
                                                          width: 'auto',
                                                          height: 'auto',
                                                          maxWidth: '100%',
                                                          objectFit: 'scale-down',
                                                          maxHeight: '200px',
                                                          minHeight: '200px',
                                                        }}
                                                      />
                                                    </button>
                                                  </Tooltip>
                                                  <Box
                                                    sx={{
                                                      p: 2,
                                                      whiteSpace: 'pre-wrap',
                                                      fontFamily: 'monospace',
                                                      color: '#000',
                                                      position: 'relative',
                                                      borderTop: (theme) =>
                                                        `1px solid ${theme.palette.secondary.main}`,
                                                      backgroundColor: (theme) =>
                                                        theme.palette.mode === 'dark'
                                                          ? theme.palette.grey[900]
                                                          : theme.palette.grey[100],
                                                      width: 'fit-content',
                                                      maxWidth: '200%',
                                                      margin: '0 auto',
                                                      minHeight: '250px',
                                                    }}
                                                  >
                                                    <Typography
                                                      variant="body2"
                                                      component="pre"
                                                      sx={{
                                                        margin: 0,
                                                        fontFamily: 'monospace',
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                        color: '#000',
                                                        textAlign: 'center',
                                                        width: '100%',
                                                      }}
                                                    >
                                                      <Box
                                                        sx={{
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          justifyContent: 'center',
                                                          gap: 1,
                                                          mb: 1,
                                                        }}
                                                      >
                                                        <Tooltip
                                                          title={item.original_rarity || 'Normal'}
                                                          placement="top"
                                                        >
                                                          <Box
                                                            sx={{
                                                              width: '12px',
                                                              height: '12px',
                                                              borderRadius: '50%',
                                                              backgroundColor: getColorFromRarity(
                                                                item.original_rarity
                                                              ),
                                                              boxShadow:
                                                                '0 2px 4px rgba(0,0,0,0.2)',
                                                            }}
                                                          />
                                                        </Tooltip>
                                                        {item.name}
                                                      </Box>
                                                      <Box sx={{ position: 'relative' }}>
                                                        {item.stats_description}
                                                        <Tooltip
                                                          title={t('common.copyToClipboard')}
                                                        >
                                                          <IconButton
                                                            onClick={() => {
                                                              navigator.clipboard.writeText(
                                                                item.stats_description
                                                              );
                                                              toast.success(
                                                                t('common.copiedToClipboard')
                                                              );
                                                            }}
                                                            size="small"
                                                            sx={{
                                                              position: 'absolute',
                                                              top: -24,
                                                              right: -8,
                                                              color: (theme) =>
                                                                theme.palette.text.secondary,
                                                              '&:hover': {
                                                                color: (theme) =>
                                                                  theme.palette.text.primary,
                                                              },
                                                            }}
                                                          >
                                                            <ContentCopyIcon fontSize="small" />
                                                          </IconButton>
                                                        </Tooltip>
                                                      </Box>
                                                    </Typography>
                                                  </Box>
                                                </Box>
                                              </div>
                                            </GridItem>
                                          ))
                                      )}
                                    </GridContainer>
                                    <PaginationControls
                                      data={topRareItems}
                                      currentPage={currentRareItemPage}
                                      setCurrentPage={setCurrentRareItemPage}
                                      itemsPerPage={8}
                                    />
                                  </div>
                                ),
                                tabName: 'dedicated-items',
                              },
                              {
                                tabButton: t('landing.fullChars.itemPacks'),
                                tabContent: (
                                  <div
                                    style={{
                                      textAlign: 'center',
                                      padding: '20px',
                                      border: '1px solid rgba(241, 198, 43, 0.3)',
                                      borderRadius: '6px',
                                    }}
                                  >
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'item-packs',
                              },
                            ]}
                          />
                        </div>
                      ),
                      tabName: 'general',
                    },
                    {
                      tabButton: (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <img
                            src={barbarianClass}
                            alt="Barbarian"
                            style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                          />
                          {t('guides.class.barbarian')}
                        </div>
                      ),
                      tabContent: (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <NavPills
                            color="primary"
                            queryKey="category"
                            alignCenter
                            tabs={[
                              {
                                tabButton: t('landing.fullChars.dedicatedItems'),
                                tabContent: (
                                  <div
                                    style={{
                                      textAlign: 'center',
                                      padding: '20px',
                                      border: '1px solid rgba(241, 198, 43, 0.3)',
                                      borderRadius: '6px',
                                    }}
                                  >
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'dedicated-items',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvpChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvp-chars',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvmChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvm-chars',
                              },
                            ]}
                          />
                        </div>
                      ),
                      tabName: 'barbarian',
                    },
                    {
                      tabButton: (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <img
                            src={sorceressClass}
                            alt="Sorceress"
                            style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                          />
                          {t('guides.class.sorceress')}
                        </div>
                      ),
                      tabContent: (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <NavPills
                            color="primary"
                            queryKey="category"
                            alignCenter
                            tabs={[
                              {
                                tabButton: t('landing.fullChars.dedicatedItems'),
                                tabContent: (
                                  <div
                                    style={{
                                      textAlign: 'center',
                                      padding: '20px',
                                      border: '1px solid rgba(241, 198, 43, 0.3)',
                                      borderRadius: '6px',
                                    }}
                                  >
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'dedicated-items',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvpChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvp-chars',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvmChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvm-chars',
                              },
                            ]}
                          />
                        </div>
                      ),
                      tabName: 'sorceress',
                    },
                    {
                      tabButton: (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <img
                            src={amazonClass}
                            alt="Amazon"
                            style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                          />
                          {t('guides.class.amazon')}
                        </div>
                      ),
                      tabContent: (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <NavPills
                            color="primary"
                            queryKey="category"
                            alignCenter
                            tabs={[
                              {
                                tabButton: t('landing.fullChars.dedicatedItems'),
                                tabContent: (
                                  <div
                                    style={{
                                      textAlign: 'center',
                                      padding: '20px',
                                      border: '1px solid rgba(241, 198, 43, 0.3)',
                                      borderRadius: '6px',
                                    }}
                                  >
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'dedicated-items',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvpChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvp-chars',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvmChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvm-chars',
                              },
                            ]}
                          />
                        </div>
                      ),
                      tabName: 'amazon',
                    },
                    {
                      tabButton: (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <img
                            src={assassinClass}
                            alt="Assassin"
                            style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                          />
                          {t('guides.class.assassin')}
                        </div>
                      ),
                      tabContent: (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <NavPills
                            color="primary"
                            queryKey="category"
                            alignCenter
                            tabs={[
                              {
                                tabButton: t('landing.fullChars.dedicatedItems'),
                                tabContent: (
                                  <div
                                    style={{
                                      textAlign: 'center',
                                      padding: '20px',
                                      border: '1px solid rgba(241, 198, 43, 0.3)',
                                      borderRadius: '6px',
                                    }}
                                  >
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'dedicated-items',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvpChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvp-chars',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvmChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvm-chars',
                              },
                            ]}
                          />
                        </div>
                      ),
                      tabName: 'assassin',
                    },
                    {
                      tabButton: (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <img
                            src={necromancerClass}
                            alt="Necromancer"
                            style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                          />
                          {t('guides.class.necromancer')}
                        </div>
                      ),
                      tabContent: (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <NavPills
                            color="primary"
                            queryKey="category"
                            alignCenter
                            tabs={[
                              {
                                tabButton: t('landing.fullChars.dedicatedItems'),
                                tabContent: (
                                  <div
                                    style={{
                                      textAlign: 'center',
                                      padding: '20px',
                                      border: '1px solid rgba(241, 198, 43, 0.3)',
                                      borderRadius: '6px',
                                    }}
                                  >
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'dedicated-items',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvpChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvp-chars',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvmChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvm-chars',
                              },
                            ]}
                          />
                        </div>
                      ),
                      tabName: 'necromancer',
                    },
                    {
                      tabButton: (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <img
                            src={paladinClass}
                            alt="Paladin"
                            style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                          />
                          {t('guides.class.paladin')}
                        </div>
                      ),
                      tabContent: (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <NavPills
                            color="primary"
                            queryKey="category"
                            alignCenter
                            tabs={[
                              {
                                tabButton: t('landing.fullChars.dedicatedItems'),
                                tabContent: (
                                  <div
                                    style={{
                                      textAlign: 'center',
                                      padding: '20px',
                                      border: '1px solid rgba(241, 198, 43, 0.3)',
                                      borderRadius: '6px',
                                    }}
                                  >
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'dedicated-items',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvpChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvp-chars',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvmChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvm-chars',
                              },
                            ]}
                          />
                        </div>
                      ),
                      tabName: 'paladin',
                    },
                    {
                      tabButton: (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <img
                            src={druidClass}
                            alt="Druid"
                            style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                          />
                          {t('guides.class.druid')}
                        </div>
                      ),
                      tabContent: (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <NavPills
                            color="primary"
                            queryKey="category"
                            alignCenter
                            tabs={[
                              {
                                tabButton: t('landing.fullChars.dedicatedItems'),
                                tabContent: (
                                  <div
                                    style={{
                                      textAlign: 'center',
                                      padding: '20px',
                                      border: '1px solid rgba(241, 198, 43, 0.3)',
                                      borderRadius: '6px',
                                    }}
                                  >
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'dedicated-items',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvpChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvp-chars',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvmChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvm-chars',
                              },
                            ]}
                          />
                        </div>
                      ),
                      tabName: 'druid',
                    },
                  ]}
                />
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
      <BubbleContainer pageKey="landing" helpText={t('help.landing')} />
    </div>
  );
}
